import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import spaceImage from '../images/space.webp';
import { authApi } from '../services/api';
import { PlayerTrialSignupStatus } from '../types/auth';
import HostedInvoiceDialog from './HostedInvoiceDialog';
import PaymentProcessingOverlay from './PaymentProcessingOverlay';
import Spinner from './Spinner';
import SubscriptionCalloutBanner from './SubscriptionCalloutBanner';
import TermsAndPrivacy from './TermsAndPrivacy';

const CheckoutForm: React.FC<{
    onSuccess: (result: any) => void;
    onError: (error: string) => void;
    isProcessing: boolean;
    setIsProcessing: (processing: boolean) => void;
    setHostedInvoiceData: (data: { url: string } | null) => void;
}> = ({ onSuccess, onError, isProcessing, setIsProcessing, setHostedInvoiceData }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [isPaymentInfoComplete, setIsPaymentInfoComplete] = useState(false);

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        if (!stripe || !elements) {
            return;
        }

        try {
            // Set processing state to show spinner and disable button
            setIsProcessing(true);

            await elements.submit();

            // Create payment method
            const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
                elements,
            });

            if (pmError) {
                onError(pmError.message || 'Failed to create payment method');
                return;
            }

            if (!paymentMethod) {
                onError('Failed to create payment method');
                return;
            }

            // Call attemptSubscriptionCreationExistingUser with the payment method ID
            const response = await authApi.attemptSubscriptionCreationExistingUser({
                paymentMethodId: paymentMethod.id,
            });

            if (response.hostedInvoiceUrl) {
                console.log('Hosted invoice URL:', response.hostedInvoiceUrl);
                // Show dialog instead of redirecting
                setHostedInvoiceData({
                    url: response.hostedInvoiceUrl,
                });
                return;
            }

            if (response.status === 'success') {
                onSuccess(response);
            } else {
                onError(response.message || 'Failed to create subscription');
            }
        } catch (err: any) {
            onError(err.message || 'An unexpected error occurred');
        } finally {
            // Reset processing state
            setIsProcessing(false);
        }
    };

    return (
        <>
            <div className='space-y-6'>
                <PaymentElement
                    className='mb-6'
                    onChange={event => {
                        setIsPaymentInfoComplete(event.complete);
                    }}
                />

                <SubscriptionCalloutBanner />

                <button
                    type='submit'
                    disabled={!stripe || !elements || isProcessing || !isPaymentInfoComplete}
                    className='w-full bg-gradient-to-r from-yellow-400 to-orange-500 !text-black py-3 px-4 rounded-lg hover:from-yellow-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transform transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium disabled:transform-none'
                    onClick={handleSubmit}
                >
                    Start Subscription ($3/month)
                </button>
                <TermsAndPrivacy />

                {/* Show what fields are still needed */}
                {!isPaymentInfoComplete && (
                    <div className='mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg'>
                        <div className='flex items-start'>
                            <svg
                                className='w-5 h-5 text-amber-600 mr-2 mt-0.5 flex-shrink-0'
                                fill='none'
                                stroke='currentColor'
                                viewBox='0 0 24 24'
                            >
                                <path
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                    strokeWidth='2'
                                    d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z'
                                ></path>
                            </svg>
                            <div className='text-sm text-amber-800'>
                                <p className='font-medium mb-1'>
                                    Complete payment information to proceed:
                                </p>
                                <ul className='space-y-1 text-xs'>
                                    <li>• Enter your payment details above</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <PaymentProcessingOverlay isVisible={isProcessing} />
        </>
    );
};

const SignupExistingUserStripe: React.FC = () => {
    const [error, setError] = useState<string>('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [successData, setSuccessData] = useState<any>(null);
    const [hostedInvoiceData, setHostedInvoiceData] = useState<{
        url: string;
    } | null>(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const loadAccountInfo = useCallback(async () => {
        try {
            setLoading(true);
            const response = await authApi.getAccountInfo();

            if (response.status === 'success' && response.account) {
                // Redirect to trial signup payment if user needs to complete trial signup
                if (response.account.trialSignupStatus === PlayerTrialSignupStatus.PLAYER_CREATED) {
                    const urlParams = new URLSearchParams(window.location.search);
                    const returnTo = urlParams.get('returnTo');
                    const redirectUrl = returnTo
                        ? `/trial-signup-payment?returnTo=${returnTo}`
                        : '/trial-signup-payment';
                    navigate(redirectUrl);
                    return;
                }
            } else {
                setError(response.message || 'Failed to load account information');
            }
        } catch (err) {
            setError('An unexpected error occurred');
        } finally {
            setLoading(false);
        }
    }, [navigate]);

    useEffect(() => {
        loadAccountInfo();
    }, [loadAccountInfo]);

    const handlePaymentSuccess = (result: any) => {
        setSuccessData(result);
        // Clear URL parameters after successful payment, but preserve returnTo if it exists
        const urlParams = new URLSearchParams(window.location.search);
        const returnTo = urlParams.get('returnTo');
        const cleanUrl = returnTo
            ? `${window.location.pathname}?returnTo=${returnTo}`
            : window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);
    };

    const handlePaymentError = (errorMessage: string) => {
        setError(errorMessage);
        setIsProcessing(false);
    };

    const handleReturnToAccount = () => {
        navigate('/account');
    };

    // Show loading indicator
    if (loading) {
        return (
            <div
                className='min-h-screen flex items-center justify-center'
                style={{
                    backgroundImage: `url(${spaceImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundAttachment: 'fixed',
                    backgroundRepeat: 'no-repeat',
                }}
            >
                <div className='flex flex-col items-center'>
                    <Spinner size='lg' color='white' />
                    <p className='mt-4 text-white'>Loading account information...</p>
                </div>
            </div>
        );
    }

    // Show hosted invoice dialog
    if (hostedInvoiceData) {
        return <HostedInvoiceDialog hostedInvoiceData={hostedInvoiceData} />;
    }

    if (successData) {
        const urlParams = new URLSearchParams(window.location.search);
        const returnTo = urlParams.get('returnTo');

        return (
            <div
                className='min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8'
                style={{
                    backgroundImage: `url(${spaceImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundAttachment: 'fixed',
                    backgroundRepeat: 'no-repeat',
                }}
            >
                <div className='max-w-md w-full mx-auto p-8 bg-white rounded-2xl shadow-xl border border-gray-100 text-center'>
                    <div className='mb-6'>
                        <div className='w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4'>
                            <svg
                                className='w-8 h-8 text-green-600'
                                fill='none'
                                stroke='currentColor'
                                viewBox='0 0 24 24'
                            >
                                <path
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                    strokeWidth='2'
                                    d='M5 13l4 4L19 7'
                                ></path>
                            </svg>
                        </div>
                    </div>
                    <h2 className='text-2xl font-bold text-gray-900 mb-2'>Payment Successful!</h2>
                    <p className='text-gray-600 mb-6'>
                        Your subscription has been activated successfully. Welcome to Puzzle Planet!
                    </p>
                    {returnTo === 'account' ? (
                        <button
                            onClick={handleReturnToAccount}
                            className='w-full bg-gradient-to-r from-yellow-400 to-orange-500 !text-black py-3 px-4 rounded-lg hover:from-yellow-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-all duration-200'
                        >
                            Back to Account
                        </button>
                    ) : (
                        <button
                            onClick={() =>
                                (window.location.href = '/game?t=' + new Date().getTime())
                            }
                            className='w-full bg-gradient-to-r from-yellow-400 to-orange-500 !text-black py-3 px-4 rounded-lg hover:from-yellow-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-all duration-200'
                        >
                            Back to Puzzle Planet
                        </button>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div
            className='min-h-screen py-8'
            style={{
                backgroundImage: `url(${spaceImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundAttachment: 'fixed',
                backgroundRepeat: 'no-repeat',
            }}
        >
            <div className='max-w-4xl mx-auto px-4'>
                <div className='text-center mb-8'>
                    <h1 className='text-4xl font-bold text-orange-500 mb-2'>
                        Upgrade Your Account
                    </h1>
                    <p className='text-lg text-white'>Complete your PuzzlePlanet subscription</p>
                </div>

                <div className='max-w-2xl mx-auto'>
                    {/* Payment Form */}
                    <div className='bg-white rounded-2xl shadow-xl border border-gray-100 p-8'>
                        <h2 className='text-2xl font-semibold text-gray-900 mb-6'>
                            Payment Information
                        </h2>

                        {error && (
                            <div className='mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg'>
                                <div className='flex items-center'>
                                    <svg
                                        className='w-5 h-5 mr-2'
                                        fill='none'
                                        stroke='currentColor'
                                        viewBox='0 0 24 24'
                                    >
                                        <path
                                            strokeLinecap='round'
                                            strokeLinejoin='round'
                                            strokeWidth='2'
                                            d='M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                                        ></path>
                                    </svg>
                                    {error}
                                </div>
                            </div>
                        )}

                        <CheckoutForm
                            onSuccess={handlePaymentSuccess}
                            onError={handlePaymentError}
                            isProcessing={isProcessing}
                            setIsProcessing={setIsProcessing}
                            setHostedInvoiceData={setHostedInvoiceData}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SignupExistingUserStripe;
